import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FiBook, FiTrendingUp, FiClock } from 'react-icons/fi';
import './theme.css';
import FlashcardsNav from './FlashcardsNav';

const Study = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUserDecks = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get user's personal flashcard collection
        const userDecksRef = collection(db, 'users', user.uid, 'flashcards');
        const userDecksSnapshot = await getDocs(userDecksRef);
        
        const userDeckIds = [];
        userDecksSnapshot.forEach((doc) => {
          userDeckIds.push(doc.id);
        });

        // Fetch the actual deck data from the library collection
        const decksData = [];
        for (const deckId of userDeckIds) {
          try {
            const deckDoc = await getDoc(doc(db, 'flashcardLibrary', deckId));
            if (deckDoc.exists()) {
              const data = deckDoc.data();
              const parsedData = JSON.parse(data.deck); // Changed from data.data to data.deck
              decksData.push({
                id: deckId,
                title: parsedData.title || 'Untitled Deck',
                subject: parsedData.subject || 'Islamic Studies',
                gradeLevel: parsedData.gradeLevel || '',
                languageMode: parsedData.languageMode || 'mixed',
                cards: parsedData.cards?.length || 0,
                createdAt: data.createdAt,
                lastEdited: data.lastEdited,
                ownerID: data.ownerID,
                tags: data.tags || [],
                public: data.public || false,
                progress: Math.floor(Math.random() * 100), // TODO: Implement real progress tracking
                category: parsedData.subject || 'Islamic Studies'
              });
            }
          } catch (parseError) {
            console.error('Error parsing deck data:', parseError);
          }
        }

        // Sort by creation date (newest first)
        decksData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDecks(decksData);
      } catch (err) {
        console.error('Error fetching user decks:', err);
        setError('Failed to load your flashcards');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDecks();
  }, [user]);

  const getCategoryColor = (subject) => {
    const colors = {
      'Islamic Studies': 'var(--gradient-primary)',
      'Quran': 'linear-gradient(135deg, #059669, #10b981)',
      'Hadith': 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
      'Fiqh': 'linear-gradient(135deg, #dc2626, #ef4444)',
      'Arabic': 'linear-gradient(135deg, #ea580c, #f97316)',
      'History': 'linear-gradient(135deg, #0891b2, #06b6d4)',
      'Aqeedah': 'linear-gradient(135deg, #059669, #10b981)',
      'Dua': 'linear-gradient(135deg, #7c3aed, #8b5cf6)'
    };
    return colors[subject] || 'var(--gradient-primary)';
  };

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <FlashcardsNav />
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="text-foreground" style={{ fontSize: 18, marginBottom: 16 }}>
              Please sign in to access study mode
            </div>
            <button className="button primary" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <FlashcardsNav />
        
        <div style={{ marginBottom: 24 }}>
          <h1 className="text-foreground" style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Study Mode</h1>
          <p className="text-muted-foreground">Choose a deck to start learning</p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="text-muted-foreground">Loading your flashcards...</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="error-message">{error}</div>
          </div>
        ) : decks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="rounded-xl border" style={{ padding: 32, background: 'var(--gradient-subtle)', maxWidth: 400, margin: '0 auto' }}>
              <div className="text-primary" style={{ fontSize: 48, marginBottom: 16 }}>
                <FiBook />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>No Flashcards to Study</h3>
              <p className="text-muted-foreground" style={{ marginBottom: 16 }}>
                Create flashcards or save some from the library to start studying
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="button primary" onClick={() => navigate('/tools/flashcards/create')}>
                  Create Flashcards
                </button>
                <button className="button outline" onClick={() => navigate('/tools/flashcards/library')}>
                  Browse Library
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-3">
            {decks.map((deck) => (
              <div key={deck.id} className="card hover:shadow-elegant" style={{ cursor: 'pointer', transition: 'var(--transition-smooth)' }}>
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div className="card-title" style={{ marginBottom: 4 }}>{deck.title}</div>
                      <div className="card-description">{deck.subject}</div>
                    </div>
                    <div 
                      className="rounded-lg" 
                      style={{ 
                        padding: 8, 
                        marginLeft: 12, 
                        background: getCategoryColor(deck.subject), 
                        color: 'white',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FiBook />
                    </div>
                  </div>
                </div>
                <div className="card-content">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14 }}>
                    <span className="text-muted-foreground">{deck.cards} cards</span>
                    <span className="badge">{deck.category}</span>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <span>Progress</span>
                      <span style={{ fontWeight: 600 }}>{deck.progress}%</span>
                    </div>
                    <div className="progress" style={{ marginTop: 8 }}>
                      <div className="bar" style={{ width: `${deck.progress}%` }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, paddingTop: 12 }}>
                    <button 
                      className="button primary" 
                      style={{ flex: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tools/flashcards/study/${deck.id}`);
                      }}
                    >
                      ▶ Study
                    </button>
                    <button 
                      className="button outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/flashcards/${deck.id}`);
                      }}
                    >
                      ⏰ Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 48, textAlign: 'center' }}>
          <div className="rounded-xl border" style={{ padding: 24, background: 'var(--gradient-subtle)' }}>
            <div className="text-primary" style={{ fontSize: 48, marginBottom: 12 }}>
              <FiTrendingUp />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Ready to Learn?</h3>
            <p className="text-muted-foreground" style={{ marginBottom: 12 }}>Select a deck above to start your Islamic education journey</p>
            <button className="button outline" onClick={() => navigate('/tools/flashcards/create')}>Create Custom Deck</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Study; 